import React, { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import SectionHead from "../ui/SectionHead";
import Reveal from "../ui/Reveal";
import { INTEL_PROMPTS, INTEL_FEATURES } from "../../data/site";
import { Sparkles, Activity, Zap, Lock, ArrowRight } from "../ui/Icons";
import "./Intelligence.css";

const ICONS = { sparkles: Sparkles, activity: Activity, zap: Zap };

function useTypewriter(text, reduce) {
  const [out, setOut] = useState(reduce ? text : "");
  const [done, setDone] = useState(reduce);
  useEffect(() => {
    if (reduce || !text) {
      setOut(text);
      setDone(true);
      return undefined;
    }
    setOut("");
    setDone(false);
    let i = 0;
    const id = setInterval(() => {
      i += 3;
      setOut(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        setDone(true);
      }
    }, 16);
    return () => clearInterval(id);
  }, [text, reduce]);
  return [out, done];
}

export function AskConsole() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [thinking, setThinking] = useState(false);
  const prompt = INTEL_PROMPTS[active];
  const [answer, done] = useTypewriter(thinking ? "" : prompt.answer, reduce);

  const pick = (i) => {
    if (i === active || thinking) return;
    setActive(i);
    setThinking(true);
    setTimeout(() => setThinking(false), reduce ? 0 : 700);
  };

  return (
    <div className="ask" aria-label="Simulated natural-language query over camera events">
      <div className="ask__head">
        <span className="dot" />
        <span className="mono">ask your cameras</span>
        <span className="ask__demo mono">simulated</span>
      </div>

      <div className="ask__prompts" role="tablist" aria-label="Example questions">
        {INTEL_PROMPTS.map((p, i) => (
          <button
            key={p.q}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={`ask__prompt ${i === active ? "is-active" : ""}`}
            onClick={() => pick(i)}
          >
            {p.q}
          </button>
        ))}
      </div>

      <div className="ask__thread">
        <div className="ask__msg ask__msg--user">{prompt.q}</div>
        <div className="ask__msg ask__msg--ai">
          {thinking ? (
            <span className="ask__thinking" aria-label="Thinking">
              <i />
              <i />
              <i />
            </span>
          ) : (
            <p className="ask__answer">
              {answer}
              {!done && <span className="ask__caret" />}
            </p>
          )}

          {done && !thinking && (
            <>
              <ul className="ask__events">
                {prompt.events.map((e) => (
                  <li key={`${e.t}-${e.what}`} className="ask__event mono">
                    <span className="ask__t">{e.t}</span>
                    <span className="ask__cam">{e.cam}</span>
                    <span className="ask__what">{e.what}</span>
                    <span className="ask__conf">{e.conf}</span>
                  </li>
                ))}
              </ul>
              <div className="ask__actions">
                <span className="mono ask__actions-label">agent can</span>
                {prompt.actions.map((a) => (
                  <span key={a} className="chip chip--accent ask__action">
                    <Zap />
                    {a}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function IntelFeatures({ compact = false }) {
  return (
    <div className={`intel__features ${compact ? "intel__features--row" : ""}`}>
      {INTEL_FEATURES.map((f, i) => {
        const Icon = ICONS[f.icon];
        return (
          <Reveal key={f.title} className="intel__card card card--hover" delay={i * 0.08}>
            <span className="icon-box">
              <Icon />
            </span>
            <h3 className="intel__title">{f.title}</h3>
            <p className="intel__text">{f.text}</p>
            <div className="chips">
              {f.chips.map((c) => (
                <span key={c} className="chip">
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

export default function Intelligence() {
  return (
    <section id="intelligence" className="section intel">
      <div className="container">
        <SectionHead
          eyebrow="Beyond detection"
          title="Cameras you can ask. Agents that act on the answer."
          lead="Detections become events. Events become answers. A vision-language layer lets your team query footage in plain language, a language model writes the reports, and AI agents carry out the follow-up, with a human in the loop where you want one."
        />

        <div className="intel__grid">
          <Reveal className="intel__console">
            <AskConsole />
          </Reveal>
          <div>
            <IntelFeatures />
            <Reveal className="intel__note" delay={0.2}>
              <Lock />
              <p>
                Private by default. Language models can run on-premise or inside your own cloud
                tenant, and agents only act within the guardrails and approvals you define.
              </p>
            </Reveal>
          </div>
        </div>

        <Reveal className="intel__flow mono" delay={0.1}>
          {["Cameras", "Vision models", "Events", "LLM / VLM", "Agents", "Your tools"].map(
            (s, i, arr) => (
              <React.Fragment key={s}>
                <span className="intel__flow-step">{s}</span>
                {i < arr.length - 1 && <ArrowRight className="intel__flow-arrow" />}
              </React.Fragment>
            ),
          )}
        </Reveal>
      </div>
    </section>
  );
}
